<script setup lang="ts">
import { computed, onMounted, watch, ref } from 'vue'

import { useSourcepointStore } from '../../stores/sourcepoint'
import { useSessionStore } from '../../stores/session'
import { useConfigStore } from '../../stores/config'
import { useClient } from '../../utils/client-only'
import { useDisallowAds } from '../../advertisement/composables/use-disallow-ads'

const disallowAds = useDisallowAds()

const sourcepointStore = useSourcepointStore()
const configStore = useConfigStore()
const sessionStore = useSessionStore()
const rsConfig = useConfigStore().rsConfig
const { isClient } = useClient()

const taboolaLazyLoading = ref(false)
const simulateContentPassOrContentPass = computed(() => sessionStore.simulateContentPassOrContentPass)
const consentedVendors = computed(() => sourcepointStore.consentedVendors)
const taboolaDE = rsConfig.consent.vendor.taboolaDE as string
const taboolaEU = rsConfig.consent.vendor.taboolaEU as string

const taboolaDeHasConsent = computed(() => {
  return consentedVendors.value.includes(taboolaDE)
})

const taboolaEuHasConsent = computed(() => {
  return consentedVendors.value.includes(taboolaEU)
})

const combinedTaboolaConsent = computed(() => {
  return taboolaDeHasConsent.value && taboolaEuHasConsent.value
})

// check for consent, if no PUR sub exists and if disallowTaboola is false
const shouldLoadTaboola = computed(() => {
  return !simulateContentPassOrContentPass.value && combinedTaboolaConsent.value && !disallowAds.value.disallowTaboola
})

// get the config for the taboola slots from {brandname}.ts
const getTaboolaSlotConfigs = computed(() => {
  const slotConfig: Array<{ mode: string, container: string, placement: string, target_type: string }> = []
  Object.entries(configStore.rsConfig.adSlotUsage.taboola.config).forEach(([, value]) => {
    if (typeof value === 'object' && value !== null) {
      slotConfig.push(value as { mode: string, container: string, placement: string, target_type: string })
    }
  })
  return slotConfig
})

const getTaboolaScript = computed(() => configStore.rsConfig.adSlotUsage.taboola.script)

function initTaboola () {
  window.intTbScript = getTaboolaScript.value
  window.intTbSlots = getTaboolaSlotConfigs.value
  window.intTbLoader = function () {
    window._taboola = window._taboola || []
    window._taboola.push({ article: 'auto' })

    const tbScript = document.createElement('script')
    tbScript.src = window.intTbScript
    tbScript.async = true
    tbScript.id = 'tb_loader_script'
    document.head.appendChild(tbScript)

    window.intTbSlots.forEach(slotConfig => {
      if (slotConfig.container === 'taboola-below-article-thumbnails') {
        window._taboola.push({
          mode: slotConfig.mode,
          container: slotConfig.container,
          placement: slotConfig.placement,
          target_type: slotConfig.target_type
        })
      }
    })

    if (window.performance && typeof window.performance.mark === 'function') {
      window.performance.mark('tbl_ic')
    }

    window._taboola.push({ flush: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', taboolaObserver)
  } else {
    taboolaObserver()
  }
}

function taboolaObserver () {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
  const loadBeforeInViewOffset = 2 * viewportHeight

  if ('IntersectionObserver' in window && taboolaLazyLoading.value) {
    const taboolaObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          window.intTbLoader()
          taboolaObserver.unobserve(entry.target)
        }
      })
    }, { root: null, rootMargin: `0px 0px ${loadBeforeInViewOffset}px 0px`, threshold: 0 })

    const targetElement = document.querySelector('#int-js-taboola')
    if (targetElement) {
      taboolaObserver.observe(targetElement)
    } else {
      console.error('Element with id "int-js-taboola" not found')
    }
  } else {
    window.intTbLoader()
  }
}

watch(shouldLoadTaboola, (newValue) => {
  if (newValue) {
    initTaboola()
  }
})

onMounted(() => {
  if (shouldLoadTaboola.value) {
    initTaboola()
  }
})
</script>

// use only the first slotconfig 'slot1' as informed by taboola only 1 slot is necessary now
<template>
  <div v-if="isClient && shouldLoadTaboola"
       id="int-js-taboola"
       class="flex w-full relative max-w-[1040px] pt-16 mr-auto md:mb-6 lg:max-w-[1320px] lg:mr-[200px] xl:ml-auto 2xl:mr-auto">
    <div :id="getTaboolaSlotConfigs[0]?.container"
         :key="`taboola-container-0`"
         class="w-full items-center" />
  </div>
</template>
